import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import type { CelebrationKind } from './celebrations';
import { CELEBRATION_DESCRIPTION_KEYS, CELEBRATION_ICONS, CELEBRATION_LABEL_KEYS } from './celebration-kinds';

interface CelebrateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The kinds offered: the ones that light or grow the card need it on the media. */
  kinds: CelebrationKind[];
  /** Puts one of this kind where the video is stopped. */
  onPick: (kind: CelebrationKind) => void;
}

/**
 * The kinds of celebration to put where the video is stopped, from the
 * editor's side column. Picking one closes the dialog; the new pin is then on
 * the timeline, to be moved or tapped to change.
 */
export function CelebrateDialog({ open, onOpenChange, kinds, onPick }: CelebrateDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="celebrate-dialog">
        <DialogHeader>
          <DialogTitle>{t('shareClimb.celebrate')}</DialogTitle>
          <DialogDescription>{t('shareClimb.celebrateHint')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {kinds.map((kind) => {
            const Icon = CELEBRATION_ICONS[kind];
            return (
              <button
                key={kind}
                type="button"
                onClick={() => { onPick(kind); onOpenChange(false); }}
                className="flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent"
                data-testid={`celebrate-${kind}`}
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="size-5" aria-hidden />
                </span>
                <span className="flex flex-col">
                  <span className="text-sm font-medium">{t(CELEBRATION_LABEL_KEYS[kind])}</span>
                  <span className="text-xs text-muted-foreground">{t(CELEBRATION_DESCRIPTION_KEYS[kind])}</span>
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
